import React, { useEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { PRODUCT_NAMES, SYSTEM_TYPES } from './constants';
import { getSystemTypeForCore } from './utils/systemTypeCache';
import './DeviceDetailsPanel.css';

const DeviceDetailsPanel = ({ device, onClose }) => {
  const panelRef = useRef(null);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener after a short delay to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!device) return null;

  const getSystemType = (systemTypeId) => {
    const parsed = parseInt(systemTypeId, 10);
    if (!isNaN(parsed) && SYSTEM_TYPES[parsed]) {
      return SYSTEM_TYPES[parsed];
    }
    const cached = getSystemTypeForCore(device.coreid);
    if (cached) {
      return SYSTEM_TYPES[cached] || 'Unknown';
    }
    return 'Unknown';
  };

  const getProductName = (productId) => PRODUCT_NAMES[Number(productId)] || 'Unknown';

  const formatDate = (publishedAt) => {
    const publishedDate = new Date(publishedAt);
    return publishedDate.toLocaleString();
  };

  // Format movement details for display
  const renderMovementDetails = () => {
    if (!device.movement) return <p>No movement data available</p>;

    const systemTypeLabel = getSystemType(device.aggregatedData["r/system_type"]);
    const isCloudBed = systemTypeLabel.includes('Cloud Bed');
    const isOfficeOrCloset = systemTypeLabel.includes('Office') || systemTypeLabel.includes('Closet');
    const mainLabel = isCloudBed ? 'bed' : 'main';
    const bedLabel = isCloudBed ? 'bed legs' : 'bed';

    // Cloud Bed display mapping
    const mapMainPosition = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed && !isOfficeOrCloset) return val;
      if (isCloudBed) {
        if (val === 'couch') return 'down';
        if (val === 'closet') return 'up';
      } else {
        if (val === 'couch') return 'closed';
        if (val === 'closet') return 'open';
      }
      return val;
    };

    const mapBedPosition = (val) => {
      if (val === 'unknown') return '???';
      if (!isCloudBed) return val;
      if (val === 'in') return 'out';
      if (val === 'out') return 'in';
      return val;
    };

    return (
      <div className="movement-details">
        <div className="movement-section">
          <h6>{mainLabel.toUpperCase()}</h6>
          <div className="movement-info">
            <p><strong>Current Position:</strong> {mapMainPosition(device.movement.main.current)}</p>
            <p><strong>Moving:</strong> {device.movement.main.moving || 'no'}</p>
            {device.movement.main.counts && (
              <div className="movement-counts">
                <strong>Live Movements:</strong>
                <ul>
                  {isCloudBed ? (
                    <>
                      <li>up: {device.movement.main.counts['closet'] ?? 0}</li>
                      <li>down: {device.movement.main.counts['couch'] ?? 0}</li>
                    </>
                  ) : isOfficeOrCloset ? (
                    <>
                      <li>open: {device.movement.main.counts['closet'] ?? 0}</li>
                      <li>close: {device.movement.main.counts['couch'] ?? 0}</li>
                      <li>manual: {device.movement.main.counts['manual'] ?? 0}</li>
                    </>
                  ) : (
                    Object.entries(device.movement.main.counts)
                      .filter(([key]) => key !== 'unknown')
                      .map(([key, value]) => (
                        <li key={key}>{key}: {value}</li>
                      ))
                  )}
                </ul>
              </div>
            )}
            {device.sfdc_data && (
              <div className="movement-counts">
              <>
              <ul>
                {device.sfdc_data.moves_last_day !== undefined && (
                  <p><strong>Moves Last Day:</strong> <li>{device.sfdc_data.moves_last_day}</li></p>
                )}
                {device.sfdc_data.moves_last_month !== undefined && (
                  <p><strong>Moves Last Month:</strong> <li>{device.sfdc_data.moves_last_month}</li></p>
                )}
              </ul>
              </>
              </div>
            )}
          </div>
        </div>

        {device.movement.hasBed && device.movement.bed && !isOfficeOrCloset && (
          <div className="movement-section">
            <h6>{bedLabel.toUpperCase()}</h6>
            <div className="movement-info">
              <p><strong>Current Position:</strong> {mapBedPosition(device.movement.bed.current)}</p>
              <p><strong>Moving:</strong> {device.movement.bed.moving || 'no'}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="panel-overlay" onClick={onClose}></div>
      <div className="device-details-panel" ref={panelRef}>
        <div className="panel-header">
          <h3>Device Details</h3>
          <button className="close-button" onClick={onClose} title="Close (Esc)">
            <FiX />
          </button>
        </div>

        <div className="panel-content">
          {/* Device Overview */}
          <section className="panel-section">
            <h4>Overview</h4>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Device ID:</span>
                <span className="info-value">
                  <a 
                    href={`https://console.particle.io/${device.productid}/devices/${device.coreid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {device.coreid}
                  </a>
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">System Type:</span>
                <span className="info-value">{getSystemType(device.aggregatedData["r/system_type"])}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Product:</span>
                <span className="info-value">{getProductName(device.productid)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Firmware:</span>
                <span className="info-value">v{device.fw_version}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`info-value status-${device.aggregatedData["k/status"] !== "offline" ? "online" : "offline"}`}>
                  {device.aggregatedData["k/status"] !== "offline" ? "Online" : "Offline"}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Heard:</span>
                <span className="info-value">{formatDate(device.published_at)}</span>
              </div>
            </div>
          </section>

          {/* SFDC Information */}
          {device.sfdc_data && (
            <section className="panel-section">
              <h4>Salesforce Data</h4>
              <div className="info-grid">
                {device.sfdc_data.building_name && (
                  <div className="info-item">
                    <span className="info-label">Building:</span>
                    <span className="info-value">
                      {device.sfdc_data.building_url ? (
                        <a 
                          href={`https://oriliving.lightning.force.com/lightning/r/Building__c/${device.sfdc_data.building_url}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {device.sfdc_data.building_name}
                        </a>
                      ) : (
                        device.sfdc_data.building_name
                      )}
                    </span>
                  </div>
                )}
                {device.sfdc_data.apt_number && (
                  <div className="info-item">
                    <span className="info-label">Apartment Number:</span>
                    <span className="info-value">{device.sfdc_data.apt_number}</span>
                  </div>
                )}
                {device.sfdc_data.system_id && (
                  <div className="info-item">
                    <span className="info-label">SFDC System:</span>
                    <span className="info-value">
                      {device.sfdc_data.asset_url ? (
                        <a 
                          href={`https://oriliving.lightning.force.com/lightning/r/Asset/${device.sfdc_data.asset_url}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {device.sfdc_data.system_id}
                        </a>
                      ) : (
                        device.sfdc_data.system_id
                      )}
                    </span>
                  </div>
                )}
                {device.sfdc_data.sku && (
                  <div className="info-item">
                    <span className="info-label">SKU:</span>
                    <span className="info-value">{device.sfdc_data.sku}</span>
                  </div>
                )}
                {device.sfdc_data.status && (
                  <div className="info-item">
                    <span className="info-label">Install Status:</span>
                    <span className="info-value">{device.sfdc_data.status}</span>
                  </div>
                )}
                {device.sfdc_data.install_date && (
                  <div className="info-item">
                    <span className="info-label">Install Date:</span>
                    <span className="info-value">{device.sfdc_data.install_date}</span>
                  </div>
                )}
                {device.sfdc_data.start && (
                  <div className="info-item">
                    <span className="info-label">First Seen:</span>
                    <span className="info-value">{device.sfdc_data.start}</span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Movement Tracking */}
          {device.movement && (
            <section className="panel-section">
              <h4>Movement Tracking</h4>
              {renderMovementDetails()}
            </section>
          )}

          {/* Aggregated Data */}
          <section className="panel-section">
            <h4>Aggregated Data</h4>
            <div className="aggregated-data-list">
              {Object.entries(device.aggregatedData)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, value]) => (
                  <div key={key} className="data-item">
                    <span className="data-key">{key}:</span>
                    <span className="data-value">
                      {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                    </span>
                  </div>
                ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default DeviceDetailsPanel;
